import { useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { useSession } from "@/platform/auth/use-session";
import { fetchProgressOverview, type ProgressOverview } from "@/tenants/einstein/features/analytics/api";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

const DISCIPLINE_LABELS: Record<string, string> = {
  portugues: "Português",
  matematica: "Matemática",
  biologia: "Biologia",
  quimica: "Química",
  fisica: "Física",
  ingles: "Inglês",
  historia: "História",
  geografia: "Geografia",
};

const HEATMAP_COLORS = [
  "var(--color-border)", // 0
  "rgba(0,0,0,0.15)", // 1-2
  "rgba(0,0,0,0.3)",
  "rgba(0,0,0,0.55)",
  "var(--color-foreground)", // 7+
] as const;

function heatColor(n: number): string {
  if (n === 0) return HEATMAP_COLORS[0];
  if (n <= 2) return HEATMAP_COLORS[1];
  if (n <= 4) return HEATMAP_COLORS[2];
  if (n <= 6) return HEATMAP_COLORS[3];
  return HEATMAP_COLORS[4];
}

export function ProgressPage() {
  const { session } = useSession();
  const [data, setData] = useState<ProgressOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) return;
    setLoading(true);
    fetchProgressOverview(session.user.id)
      .then(setData)
      .catch((err) => console.error("[progress] fetch", err))
      .finally(() => setLoading(false));
  }, [session]);

  const lineData = useMemo(() => {
    if (!data) return [];
    return data.sessions
      .filter((s) => s.raw_score != null)
      .map((s) => ({
        date: format(parseISO(s.ended_at), "d MMM", { locale: ptBR }),
        score: s.raw_score ?? 0,
        kind: s.kind,
      }));
  }, [data]);

  const barData = useMemo(() => {
    if (!data) return [];
    return data.latestByDiscipline.map((d) => ({
      label: DISCIPLINE_LABELS[d.discipline] ?? d.discipline,
      accuracy: Math.round(d.accuracy * 100),
      correct: d.correct,
      total: d.total,
    }));
  }, [data]);

  if (loading || !data) {
    return (
      <ProgressShell>
        <Badge variant="accentSolid">Evolução</Badge>
        <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold">
          Cruzando seus dados…
        </h1>
      </ProgressShell>
    );
  }

  if (data.totalSessions === 0) {
    return (
      <ProgressShell>
        <Badge variant="accentSolid">Evolução</Badge>
        <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
          Ainda sem dados.
        </h1>
        <p className="mt-3 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] text-pretty">
          Faça o diagnóstico — em duas horas você passa a ter série temporal de acerto, ranking por
          disciplina e mapa de atividade.
        </p>
      </ProgressShell>
    );
  }

  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="mint"
        className="pointer-events-none absolute -right-32 top-80 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-16 md:py-20">
        <header className="animate-fade-up">
          <Badge variant="accentSolid">
            <Sparkles className="size-3" aria-hidden />
            Evolução
          </Badge>
          <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
            <span data-tabular="true" className="text-gradient-accent">
              {data.totalSessions}
            </span>{" "}
            sessão{data.totalSessions === 1 ? "" : "ões"} concluída
            {data.totalSessions === 1 ? "" : "s"}.
          </h1>
          <p className="mt-2 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)]">
            Taxa de acerto geral{" "}
            <span data-tabular="true" className="font-semibold text-[color:var(--color-foreground)]">
              {(data.overallAccuracy * 100).toFixed(0)}%
            </span>{" "}
            em{" "}
            <span data-tabular="true" className="font-semibold text-[color:var(--color-foreground)]">
              {data.totalAttempts}
            </span>{" "}
            tentativas.
          </p>
        </header>

        <section className="mt-10 grid gap-4 md:grid-cols-3">
          <Stat label="Sessões" value={data.totalSessions.toString()} tone="violet" />
          <Stat label="Tentativas" value={data.totalAttempts.toString()} tone="mint" />
          <Stat label="Acerto geral" value={`${(data.overallAccuracy * 100).toFixed(0)}%`} tone="pink" />
        </section>

        <ChartCard
          tone="violet"
          title="Acerto por sessão"
          description="Pontuação bruta nas sessões concluídas (diagnóstico ou simulado)."
        >
          {lineData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={lineData} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="var(--color-muted-foreground)" fontSize={11} />
                <YAxis stroke="var(--color-muted-foreground)" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "var(--color-card)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="var(--color-accent)"
                  strokeWidth={3}
                  dot={{ r: 5, fill: "var(--color-accent)", strokeWidth: 0 }}
                  activeDot={{ r: 7, fill: "var(--color-accent)", strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Precisa de pelo menos 2 sessões pra mostrar série temporal." />
          )}
        </ChartCard>

        <ChartCard
          tone="pink"
          title="Acerto por disciplina"
          description="Consolidado de todas as sessões concluídas."
        >
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={barData} layout="vertical" margin={{ top: 8, right: 16, bottom: 0, left: 16 }}>
                <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} stroke="var(--color-muted-foreground)" fontSize={11} />
                <YAxis
                  dataKey="label"
                  type="category"
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  width={90}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--color-card)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                  formatter={(value: number, _name, entry) => [
                    `${value}% (${entry.payload.correct}/${entry.payload.total})`,
                    "Acerto",
                  ]}
                />
                <Bar dataKey="accuracy" radius={[0, 999, 999, 0]}>
                  {barData.map((d, i) => {
                    const color =
                      d.accuracy >= 70
                        ? "var(--color-mint)"
                        : d.accuracy >= 40
                          ? "var(--color-accent)"
                          : "var(--color-pink)";
                    return <Cell key={i} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sem dados ainda." />
          )}
        </ChartCard>

        <ChartCard
          tone="mint"
          title="Atividade — últimos 30 dias"
          description="Cada quadrado = um dia. Cor mais escura = mais questões respondidas."
        >
          <div className="grid grid-cols-15 gap-1.5 sm:grid-cols-30">
            {data.activity30d.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.attempts} questões`}
                className="aspect-square rounded-[5px] transition-transform hover:scale-110"
                style={{ background: heatColor(d.attempts) }}
              />
            ))}
          </div>
          <p className="mt-5 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
            Consistência &gt; intensidade. 30 minutos por dia compostos por 6 meses bate qualquer
            sprint de fim de semana.
          </p>
        </ChartCard>

        {data.discursivePractice.length > 0 ? (
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)]">
            <Badge variant="outline">Discursivas treinadas</Badge>
            <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
              Correções avulsas em /redacao e /discursivas, agregadas por disciplina.
            </p>
            <ul className="mt-5 space-y-2">
              {data.discursivePractice.map((d) => (
                <li
                  key={d.discipline}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-[--color-border] bg-[color:var(--color-paper)] p-4"
                >
                  <span className="text-[length:var(--text-body-sm)] font-medium capitalize">
                    {DISCIPLINE_LABELS[d.discipline] ?? d.discipline}
                  </span>
                  <span className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                    {d.attempts} {d.attempts === 1 ? "correção" : "correções"} · média{" "}
                    <span className="font-medium text-[color:var(--color-foreground)] tabular-nums">
                      {d.avgScore.toLocaleString("pt-BR")} pts
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {data.sessions.length > 0 ? (
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)]">
            <Badge variant="outline">Histórico</Badge>
            <ul className="mt-5 space-y-2">
              {[...data.sessions]
                .reverse()
                .slice(0, 10)
                .map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] px-4 py-3 text-[length:var(--text-body-sm)]"
                  >
                    <span className="flex items-center gap-3">
                      <Badge variant="accent">{s.kind}</Badge>
                      {format(parseISO(s.ended_at), "d 'de' MMM yyyy", { locale: ptBR })}
                    </span>
                    <span className="text-[color:var(--color-muted-foreground)]" data-tabular="true">
                      {s.raw_score != null ? `${s.raw_score} pts` : "—"} ·{" "}
                      {s.duration_seconds ? `${Math.floor(s.duration_seconds / 60)} min` : "—"}
                    </span>
                  </li>
                ))}
            </ul>
          </section>
        ) : null}
      </Container>
    </div>
  );
}

type Tone = "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";
const TONE_BADGE: Record<Tone, "accent" | "pink" | "mint" | "yellow" | "coral" | "sky"> = {
  violet: "accent",
  pink: "pink",
  mint: "mint",
  yellow: "yellow",
  coral: "coral",
  sky: "sky",
};

function ProgressShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Container size="wide" className="relative py-16 md:py-20 animate-fade-up">
        {children}
      </Container>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: Tone }) {
  return (
    <div className="lift rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)]">
      <Badge variant={TONE_BADGE[tone]}>{label}</Badge>
      <p
        data-tabular="true"
        className="mt-4 font-display text-[length:var(--text-display-md)] font-bold leading-none tracking-[var(--text-display-md--letter-spacing)]"
      >
        {value}
      </p>
    </div>
  );
}

function ChartCard({
  tone,
  title,
  description,
  children,
}: {
  tone: Tone;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)]">
      <Badge variant={TONE_BADGE[tone]}>{title}</Badge>
      <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">{description}</p>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-dashed border-[--color-border] bg-[color:var(--color-paper)] p-10 text-center">
      <p className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">{label}</p>
    </div>
  );
}
